import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { BASE_URL_V2 } from 'shared/constants';

export const useProductIQApi = () => {
  const { axios } = useAxios(BASE_URL_V2);

  const fetchProductIQOverview = async (vendorDomain: string) => {
    try {
      const res = await axios.get(`/vendor/overview`, {
        params: {
          vendorDomain: vendorDomain
        }
      });
      if (res?.status === 200) {
        return res?.data?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch supplier briefing'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const fetchProductIQProducts = async (vendorDomain: string, page?: number, size?: number) => {
    try {
      const res = await axios.get(`/vendor/products`, {
        params: {
          vendorDomain: vendorDomain,
          page: page,
          size: size
        }
      });
      if (res?.status === 200) {
        return res?.data?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch supplier briefing'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const compareProducts = async (productName: string, vendorDomain: string) => {
    try {
      const res = await axios.get(`/vendor/product-comparison`, {
        params: {
          productName: productName,
          vendorDomain: vendorDomain
        }
      });
      if (res?.status === 200) {
        return res?.data?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to compare products'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const fetchProduct = async (productName: string, vendorDomain: string) => {
    try {
      const res = await axios.get(`/vendor/product`, {
        params: {
          productName: productName,
          vendorDomain: vendorDomain
        }
      });
      if (res?.status === 200) {
        return res?.data;
      }
      if (res?.status === 404) {
        return Promise.reject(Error(res?.data?.overview || 'Failed to fetch product'));
      }
      return Promise.reject(Error(res?.data?.overview || 'Failed to fetch product'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Update Product IQ overview by vendor domain
   * @param {string} vendorDomain - The vendor domain (e.g., "microsoft.com")
   * @param {object} overviewData - The overview data object
   * @param {string} overviewData.company - The company name
   * @param {string} overviewData.companyOverview - The company overview in markdown format
   * @param {string} [overviewData.snapshotDate] - Optional snapshot date
   * @param {string[]} [overviewData.sources] - Optional array of sources
   * @param {number} [overviewData.confidence] - Optional confidence score
   * @returns {Promise<any>} A promise that resolves with the updated overview data if successful
   */
  const updateProductIQOverview = async (
    vendorDomain: string,
    overviewData: {
      company: string;
      companyOverview: string;
      snapshotDate?: string;
      sources?: string[];
      confidence?: number;
    }
  ): Promise<any> => {
    try {
      const res = await axios.put(`/vendor/overview`, {
        vendorDomain,
        overviewData
      });
      // Check for success status - if status is 200 or success, consider it successful
      if (res?.status === 200 || res?.data?.status === 'success') {
        return res.data?.data || res.data;
      }
      // Only reject if we have an explicit error message
      const errorMessage = res?.data?.message || 'Failed to update Product IQ overview';
      return Promise.reject(Error(errorMessage));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  return {
    fetchProductIQOverview,
    fetchProductIQProducts,
    compareProducts,
    fetchProduct,
    updateProductIQOverview
  };
};
